import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';

export type MuqriRequest = {
  uid: string;
  displayName: string;
  note: string;
  status: 'pending' | 'approved' | 'rejected';
};

export type DirectoryUser = {
  uid: string;
  displayName: string;
  phone: string;
  age: number | null;
  gender: 'male' | 'female' | null;
  isMuqri: boolean;
  isAdmin: boolean;
  /** Where this pupil sits in school — what selects their curriculum. */
  country: string | null;
  grade: number | null;
};

/**
 * Whether the signed-in user is an admin.
 *
 * Fails closed for the same reasons useMuqriRole does, and the stakes are
 * higher: `loading` starts true and `isAdmin` false, and a listener error —
 * a missing document, a permission problem — lands in the same false state
 * rather than holding a previous true. The rules are the gate; this only
 * decides whether the portal is offered.
 */
export function useAdminRole(uid: string | null): { isAdmin: boolean; loading: boolean } {
  const [state, setState] = useState<{ uid: string | null; isAdmin: boolean; loading: boolean }>(
    { uid: null, isAdmin: false, loading: true }
  );
  useEffect(() => {
    setState({ uid, isAdmin: false, loading: true });
    if (!uid) { setState({ uid, isAdmin: false, loading: false }); return; }
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => setState({ uid, isAdmin: snap.data()?.roles?.admin === true, loading: false }),
      () => setState({ uid, isAdmin: false, loading: false })
    );
  }, [uid]);
  // The uid arrives one render BEFORE the effect can react to it, and until
  // then `state` still answers the PREVIOUS uid — for a signed-out reader,
  // 'not an admin, and we are done'. A guard reading that verdict redirects,
  // which is what sent /admin/tracks to /unauthorized on every refresh. An
  // answer about somebody else is not an answer about this reader.
  if (state.uid !== uid) return { isAdmin: false, loading: uid !== null };
  return { isAdmin: state.isAdmin, loading: state.loading };
}

function toRequest(uid: string, data: Record<string, unknown>): MuqriRequest {
  const raw = data.status;
  return {
    uid,
    displayName: String(data.displayName ?? ''),
    note: String(data.note ?? ''),
    status: raw === 'approved' || raw === 'rejected' ? raw : 'pending',
  };
}

/**
 * The pending queue, live.
 *
 * Live rather than fetched once because two admins can be looking at the same
 * list: one decides, and the other's row should leave rather than sit there
 * inviting a second decision on a request that is no longer pending.
 */
export function listenToPendingRequests(
  cb: (requests: MuqriRequest[]) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    query(collection(db, 'muqriRequests'), where('status', '==', 'pending')),
    (snap) => cb(snap.docs.map((d) => toRequest(d.id, d.data() as Record<string, unknown>))),
    (e) => onError?.(e)
  );
}

/**
 * The decided requests — the «مقبولة» and «مرفوضة» filters on the requests
 * tab (redesign a1). Same collection, same admin read rule, filtered on the
 * other two statuses. Read only; a decision is never re-made from here.
 */
export function listenToDecidedRequests(
  status: 'approved' | 'rejected',
  cb: (requests: MuqriRequest[]) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    query(collection(db, 'muqriRequests'), where('status', '==', status)),
    (snap) => cb(snap.docs.map((d) => toRequest(d.id, d.data() as Record<string, unknown>))),
    (e) => onError?.(e)
  );
}

function toDirectoryUser(uid: string, data: Record<string, unknown>): DirectoryUser {
  const roles = (data.roles ?? {}) as Record<string, unknown>;
  const gender = data.gender;
  return {
    uid,
    displayName: String(data.displayName ?? ''),
    phone: String(data.phone ?? ''),
    age: typeof data.age === 'number' ? data.age : null,
    gender: gender === 'male' || gender === 'female' ? gender : null,
    // === true, never truthiness: the same slip the rules test pins. A role
    // seeded by hand as the STRING 'true' must not read as held here either,
    // or the directory would show a badge the rules refuse to honour.
    isMuqri: roles.muqri === true,
    isAdmin: roles.admin === true,
    country: typeof data.country === 'string' && data.country ? data.country : null,
    grade: Number.isInteger(data.grade) ? (data.grade as number) : null,
  };
}

/** The whole directory, live. Small by construction — one document per user. */
export function listenToDirectory(
  cb: (users: DirectoryUser[]) => void,
  onError?: (e: Error) => void
): () => void {
  return onSnapshot(
    collection(db, 'users'),
    (snap) => cb(snap.docs.map((d) => toDirectoryUser(d.id, d.data() as Record<string, unknown>))),
    (e) => onError?.(e)
  );
}

/**
 * Approve or reject — a CALLABLE, never a write.
 *
 * The rules refuse an admin every write to roles and to a request's status,
 * so this is the only path. The server re-checks the CALLER's stored admin
 * flag; being able to reach this screen proves nothing to it.
 */
/**
 * Moves a pupil to another grade — م-2-06.
 *
 * Enrolment is derived from (country, grade), so this re-enrols them by
 * definition; their turns, marks and coverage are keyed by uid and are not
 * touched. A callable because it is somebody ELSE's document, which the
 * Firestore rules do not let even an admin write.
 */
export async function setStudentSchooling(
  uid: string, country: string, grade: number,
): Promise<void> {
  const call = httpsCallable<{ uid: string; country: string; grade: number }, unknown>(
    getFunctions(app, FUNCTIONS_REGION),
    'setStudentSchooling',
  );
  await call({ uid, country, grade });
}

export async function decideMuqriRequest(
  uid: string, decision: 'approved' | 'rejected'
): Promise<void> {
  const call = httpsCallable<{ uid: string; decision: string }, { ok: boolean }>(
    getFunctions(app, FUNCTIONS_REGION),
    'approveMuqri'
  );
  await call({ uid, decision });
}
